"use client";

// THE WRITES THE SERVER DID NOT TAKE, held where somebody can see them.
//
// Every mutation in this app is optimistic: the in-memory Map is updated and
// rendered, and the POST to /api/store goes out behind it. That is what makes
// the UI instant, and it is fine right up until the POST is refused — because
// fetch() resolves on a 401 the same way it resolves on a 200, so the old code
// logged a line to a console nobody has open and the screen went on showing a
// change that does not exist anywhere but this tab.
//
// This module is the other half of that bargain. A refused write lands here
// instead of evaporating, and it stays here until it either lands or the person
// who made it has been told, by name, that it did not.
//
// ─── WHY THE VALUE IS KEPT, NEVER REVERTED ─────────────────────────────────
//
// The obvious fix is to roll the store back to what the server has. It is the
// wrong fix, and the reason is the case that matters most: a creator who has
// typed four paragraphs into a script. Reverting is deleting their work in front
// of them to resolve a disagreement they were not party to — and the retry they
// would then want is impossible, because the text it would have retried is gone.
//
// So the rule is: WE NEVER THROW AWAY WHAT THE USER MADE. The queue holds the
// exact value that failed, the notice offers one click to send it again, and if
// it never goes through they still have the words on screen to copy somewhere
// safe. An inconsistency the user can SEE and act on beats a consistency bought
// with their work.
//
// ─── ONE ENTRY PER ROW ──────────────────────────────────────────────────────
//
// Keyed `${kind}:${id}`, and a second failure on the same row REPLACES the
// first. A draft autosaving every 800ms against a server that is refusing would
// otherwise pile up fifty entries for one document, forty-nine of them holding
// text that has since been typed over. The newest value is the one on screen and
// therefore the only one worth retrying.

import { makeRef, reportClientError } from "./client-error-report";

/** Why the write did not land. The two cases read differently to a human. */
export type WriteFailureReason = "refused" | "unreachable";

export interface WriteFailure {
  /** `${kind}:${id}` — the row, and this queue's identity for it. */
  key: string;
  kind: string;
  id: string;
  /** HTTP verb of the write that failed, for the log and for the retry. */
  op: "write" | "delete";
  reason: WriteFailureReason;
  /** The status the server answered with. Absent when it never answered. */
  status?: number;
  /** The id this failure was filed under, server-side. Not shown to the user. */
  ref: string;
  /** Sends the same write again. Resolves whether or not it worked. */
  retry: () => Promise<void>;
}

const failures = new Map<string, WriteFailure>();
const listeners = new Set<() => void>();

// useSyncExternalStore demands a snapshot that is reference-stable until
// something actually changes — return a fresh array on every call and React
// re-renders forever. Rebuilt once per mutation, handed out unchanged in
// between.
let snapshot: WriteFailure[] = [];

function publish(): void {
  snapshot = Array.from(failures.values());
  listeners.forEach((l) => l());
}

export function subscribeWriteFailures(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getWriteFailures(): WriteFailure[] {
  return snapshot;
}

/**
 * Is this row currently stuck?
 *
 * FOR THE FORMS THAT CLOSE THEMSELVES. Most writes are autosaves and the notice
 * is the whole story — nothing was going to happen on screen either way. A form
 * with a Save button is different: it closes, and closing IS its claim that the
 * write worked. Those callers await the persist and ask this before they take
 * that step, so a refused save leaves the box open with the rows still in it and
 * the button live. That button is then the retry, which is the one the person
 * was already reaching for.
 */
export function hasWriteFailure(kind: string, id: string): boolean {
  return failures.has(`${kind}:${id}`);
}

/** Test seam, and the reset the sign-out path needs. */
export function clearWriteFailures(): void {
  if (failures.size === 0) return;
  failures.clear();
  publish();
}

/**
 * File a refused write, and tell the server about it in the same breath.
 *
 * THE REPORT GOES OUT NOW, NOT WHEN THE MODAL IS DISMISSED. The realistic end of
 * this story is a closed tab: whatever we want in the log has to be in the log
 * before the person decides they are done with us. reportClientError beacons it,
 * which is the one transport that outlives the document.
 *
 * The REF is minted here rather than taken from the reporter so that every
 * filed failure has one even in the cases the reporter declines to send — a
 * repeat, or the sixth report on a page. It is how the log and this queue name
 * the same event; the notice no longer prints it, and nobody is asked to read it
 * off a screen (see the note beside the box that used to).
 */
export function recordWriteFailure(
  input: Omit<WriteFailure, "ref">,
): WriteFailure {
  const ref = makeRef();
  const failure: WriteFailure = { ...input, ref };
  failures.set(failure.key, failure);

  reportClientError({
    error: new Error(
      `${input.op} ${input.key} was not saved — ${
        input.reason === "refused" ? `/api/store responded ${input.status}` : "the request never completed"
      }`,
    ),
    source: "write-refused",
    ref,
    context: {
      kind: input.kind,
      id: input.id,
      op: input.op,
      reason: input.reason,
      ...(input.status === undefined ? {} : { status: input.status }),
    },
  });

  publish();
  return failure;
}

/** Drop one row's failure, because its write finally landed. */
export function resolveWriteFailure(key: string): void {
  if (!failures.delete(key)) return;
  publish();
}

/**
 * Send every queued write again, and answer how many are still stuck.
 *
 * SEQUENTIAL, NOT A FAN-OUT. These retries happen when something is already
 * wrong — an expired session, a server mid-restart — and firing twenty POSTs at
 * a box that is refusing all of them is how a bad minute becomes a rate limit.
 * They are also usually all going to answer the same way, so the first one
 * arriving late costs nothing that matters.
 *
 * Each retry runs the ORIGINAL write path, so a second failure re-files itself
 * here through recordWriteFailure and replaces its own entry. A success calls
 * resolveWriteFailure and the row leaves the queue. Nothing in this function has
 * to know which of those happened.
 */
export async function retryWriteFailures(): Promise<number> {
  // A snapshot, not the live map: a retry that fails re-files itself while the
  // loop is still running.
  for (const failure of Array.from(failures.values())) {
    await failure.retry();
  }
  return failures.size;
}
